import { useState } from "react";
import { api } from "@/lib/api";
import { memberBalance } from "@/lib/membership";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const GOLD = "#C9A227";
const CHOCOLATE = "#3D2817";
const MAROON = "#4A0E17";
const GOLD_SOFT = "#D4AF37";

const inr = (n) => `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const navBtn =
    "border-[#E8E4DE] bg-white text-[#3D2817] shadow-sm hover:border-[#D4AF37]/45 hover:bg-[#FFFEF9]";

const fieldClass =
    "border-[#E8E4DE] bg-[#FFFEF9] focus-visible:border-[#D4AF37]/55 focus-visible:ring-[#D4AF37]/20";

function GoldDivider({ className = "max-w-[140px]" }) {
    return (
        <div className={`flex items-center ${className}`}>
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, transparent, ${GOLD_SOFT}B3, transparent)` }} />
            <span className="px-2.5 text-[10px] leading-none" style={{ color: GOLD }}>◆</span>
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, transparent, ${GOLD_SOFT}B3, transparent)` }} />
        </div>
    );
}

export default function MembershipCollectDialog({ member, onClose, onSaved }) {
    const balance = memberBalance(member);
    const [amount, setAmount] = useState(balance > 0 ? String(balance) : "");
    const [paymentMode, setPaymentMode] = useState("cash");
    const [paymentRef, setPaymentRef] = useState("");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    const payAmount = Math.min(Number(amount) || 0, balance);

    const submit = async (e) => {
        e.preventDefault();
        if (payAmount <= 0) return;
        setBusy(true);
        try {
            const r = await api.post(`/customer-memberships/${member.id}/collect`, {
                amount: payAmount,
                payment_mode: paymentMode,
                payment_ref: paymentRef.trim() || undefined,
                notes: notes.trim() || undefined,
            });
            const bal = r.data?.balance_due;
            toast.success(bal > 0 ? `Installment recorded · Balance ${inr(bal)}` : "Membership fully paid");
            onSaved(r.data);
        } catch (err) {
            const d = err.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Collection failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <DialogContent className="scrollbar-dialog max-h-[min(90vh,640px)] overflow-y-auto overscroll-contain rounded-xl border-[#E8E4DE] p-6 pr-5 sm:max-w-md">
            <DialogHeader>
                <DialogTitle className="font-serif text-2xl font-bold" style={{ color: MAROON }}>
                    Collect installment
                </DialogTitle>
                <GoldDivider className="mt-2 max-w-[140px]" />
                <p className="pt-1 text-sm font-medium text-[#5C534C]">
                    {member.customer?.full_name} · <span style={{ color: GOLD }}>{member.plan?.name}</span>
                </p>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 pt-2">
                <div className="rounded-xl border border-[#E8E4DE] bg-[#FFFEF9] p-4 text-sm">
                    <div className="flex justify-between font-medium text-[#8A8178]">
                        <span>Balance due</span>
                        <span className="font-serif text-lg font-bold text-[#C84B31]">{inr(balance)}</span>
                    </div>
                </div>

                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>
                        Amount collecting (₹)
                    </Label>
                    <Input
                        type="number"
                        min={0.01}
                        max={balance}
                        step="0.01"
                        className={fieldClass}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        data-testid="collect-amount"
                    />
                    <div className="flex flex-wrap gap-2">
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className={`h-7 text-[11px] ${navBtn}`}
                            onClick={() => setAmount(String(Math.round(balance / 2 * 100) / 100))}
                        >
                            Half
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className={`h-7 text-[11px] ${navBtn}`}
                            onClick={() => setAmount(String(balance))}
                        >
                            Full balance
                        </Button>
                    </div>
                </div>

                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Payment method</Label>
                    <Select value={paymentMode} onValueChange={setPaymentMode}>
                        <SelectTrigger className={fieldClass} data-testid="collect-payment-mode">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="cash">Cash</SelectItem>
                            <SelectItem value="upi">UPI</SelectItem>
                            <SelectItem value="card">Card</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {(paymentMode === "upi" || paymentMode === "card") && (
                    <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Reference</Label>
                        <Input
                            className={fieldClass}
                            value={paymentRef}
                            onChange={(e) => setPaymentRef(e.target.value)}
                            data-testid="collect-payment-ref"
                        />
                    </div>
                )}

                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Notes (optional)</Label>
                    <Input className={fieldClass} value={notes} onChange={(e) => setNotes(e.target.value)} />
                </div>

                <DialogFooter className="gap-2 pt-2">
                    <Button type="button" variant="outline" className={navBtn} onClick={onClose}>Cancel</Button>
                    <Button
                        type="submit"
                        className="bg-[#4A0E17] font-semibold hover:bg-[#3a0b12]"
                        disabled={busy || payAmount <= 0}
                        data-testid="collect-submit"
                    >
                        {busy ? "Processing…" : `Collect ${inr(payAmount)}`}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    );
}
